import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar } from "../components";
import { getReferralById, markReferralPayment } from "../../../redux/actions/apiActions";
import setGlobalLoader from "../../../redux/actions/loaderActions";
import { PROFILES, REFERRAL_STATUS, REFERRAL_PAYMENT_STATUS } from "../../../utils/constants";

const STATUS_LABELS = {
  [REFERRAL_STATUS.PENDING]: "Pending",
  [REFERRAL_STATUS.ELIGIBLE]: "Eligible",
  [REFERRAL_STATUS.AWAITING_PAYMENT]: "Awaiting Payment",
  [REFERRAL_STATUS.PAID_BOTH]: "Paid",
  [REFERRAL_STATUS.EXPIRED]: "Expired",
};

const STATUS_COLORS = {
  [REFERRAL_STATUS.PENDING]: "default",
  [REFERRAL_STATUS.ELIGIBLE]: "info",
  [REFERRAL_STATUS.AWAITING_PAYMENT]: "warning",
  [REFERRAL_STATUS.PAID_BOTH]: "success",
  [REFERRAL_STATUS.EXPIRED]: "error",
};

// ── Reusable detail row ──────────────────────────────────────────────────────
const DetailRow = ({ label, value }) => (
  <>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexDirection={{ xs: "column", sm: "row" }}
      padding={2}
    >
      <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
      <Typography variant="subtitle2" fontWeight={600}>{value ?? "-"}</Typography>
    </Box>
    <Divider />
  </>
);

// ── Payment panel for one party ──────────────────────────────────────────────
const PaymentPanel = ({ party, label, payment, canMarkPayment, onSuccess, referralId }) => {
  const [transactionRef, setTransactionRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isPaid = payment?.status === REFERRAL_PAYMENT_STATUS.PAID;

  const handleMarkPaid = async () => {
    try {
      setSubmitting(true);
      await markReferralPayment(referralId, { party, transactionRef: transactionRef || null });
      toast.success(`Payment for ${label} marked as paid.`);
      setTransactionRef("");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || `Error marking payment for ${label}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 260 }}>
      <CardHeader
        title={label}
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }}
        action={
          <Chip
            size="small"
            label={isPaid ? "Paid" : "Pending"}
            color={isPaid ? "success" : "default"}
            icon={isPaid ? <CheckCircleIcon /> : undefined}
          />
        }
      />
      <CardContent>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Paid On</Typography>
            <Typography variant="body2" fontWeight={600}>
              {payment?.paidAt ? format(new Date(payment.paidAt), "MMM dd, yyyy, hh:mm a") : "-"}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Transaction Ref</Typography>
            <Typography variant="body2" fontWeight={600}>
              {payment?.transactionRef || "-"}
            </Typography>
          </Box>

          {/* Admin: mark as paid form */}
          {canMarkPayment && !isPaid && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Mark as Paid
              </Typography>
              <TextField
                label="Transaction Reference (optional)"
                size="small"
                fullWidth
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                placeholder="UTR / UPI Ref / Bank Ref"
              />
              <Button
                variant="contained"
                size="small"
                disabled={submitting}
                onClick={handleMarkPaid}
              >
                {submitting ? "Saving..." : "Confirm Payment"}
              </Button>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const ViewReferral = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const profile = useSelector((state) => state.authReducer.profile);
  const isMd = useMediaQuery(theme.breakpoints.up("md"), { defaultMatches: true });

  const [openSidebar, setOpenSidebar] = useState(false);
  const [referral, setReferral] = useState(null);

  const open = isMd ? false : openSidebar;

  const fetchReferral = async () => {
    try {
      dispatch(setGlobalLoader(true));
      const res = await getReferralById(id);
      setReferral(res?.data?.data);
      dispatch(setGlobalLoader(false));
    } catch (err) {
      dispatch(setGlobalLoader(false));
      toast.error(err?.response?.data?.message || "Error fetching referral.");
      navigate("/referrals");
    }
  };

  useEffect(() => {
    if (id) fetchReferral();
  }, [id]);

  const isAdmin = profile === PROFILES.ADMIN;
  const canMarkPayment = isAdmin && referral?.status === REFERRAL_STATUS.AWAITING_PAYMENT;

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={() => setOpenSidebar(true)} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={() => setOpenSidebar(false)}
        open={open}
        variant={isMd ? "permanent" : "temporary"}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <Box
          marginLeft={{ md: "290px", xs: "30px" }}
          marginTop={{ md: "20px", xs: "20px" }}
          marginRight={{ md: "40px", xs: "30px" }}
        >
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/referrals")}
                size="small"
                variant="outlined"
              >
                Back
              </Button>
              <Typography variant="subtitle1" fontWeight={700}>
                Referral Details
              </Typography>
              {referral?.status && (
                <Chip
                  size="small"
                  label={STATUS_LABELS[referral.status] ?? referral.status}
                  color={STATUS_COLORS[referral.status] ?? "default"}
                />
              )}
            </Box>
          </Box>

          {referral && (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* ── Referral Details Card ── */}
              <Box
                border={`2px solid ${theme.palette.divider}`}
                borderRadius={2}
                sx={{ borderStyle: "dashed" }}
              >
                <DetailRow label="Referral ID" value={referral.displayId} />
                <DetailRow label="Referrer (Inviter)" value={referral.referrerOrgId?.name} />
                <DetailRow label="Referred (Invitee)" value={referral.referredOrgId?.name} />
                <DetailRow
                  label="Status"
                  value={
                    <Chip
                      size="small"
                      label={STATUS_LABELS[referral.status] ?? referral.status}
                      color={STATUS_COLORS[referral.status] ?? "default"}
                    />
                  }
                />
                <DetailRow
                  label="Payout Amount"
                  value={referral.payoutAmount != null ? `₹${referral.payoutAmount} each` : "-"}
                />
                <DetailRow
                  label="Eligible On"
                  value={referral.eligibleAt ? format(new Date(referral.eligibleAt), "MMM dd, yyyy") : "-"}
                />
                <DetailRow
                  label="Payout Due By"
                  value={referral.payoutDueAt ? format(new Date(referral.payoutDueAt), "MMM dd, yyyy") : "-"}
                />
                <DetailRow
                  label="Created On"
                  value={referral.createdAt ? format(new Date(referral.createdAt), "MMM dd, yyyy, hh:mm a") : "-"}
                />
              </Box>

              {/* ── Payment Cards ── */}
              <Typography variant="subtitle1" fontWeight={700}>
                Payment Status
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={3}>
                <PaymentPanel
                  party="referrer"
                  label="Referrer Payment"
                  payment={referral.referrerPayment}
                  canMarkPayment={canMarkPayment && referral.referrerPayment?.status !== REFERRAL_PAYMENT_STATUS.PAID}
                  referralId={referral._id}
                  onSuccess={fetchReferral}
                />
                <PaymentPanel
                  party="referred"
                  label="Referred Payment"
                  payment={referral.referredPayment}
                  canMarkPayment={canMarkPayment && referral.referredPayment?.status !== REFERRAL_PAYMENT_STATUS.PAID}
                  referralId={referral._id}
                  onSuccess={fetchReferral}
                />
              </Box>
            </Box>
          )}
        </Box>
      </main>
    </Box>
  );
};

export default ViewReferral;
